<template>
  <nav class="ah-nav" aria-label="主导航">
    <div class="ah-nav-menu">
      <router-link
        v-for="item in pageList"
        :key="item.path"
        :to="item.path"
        class="ah-nav-item"
        :class="{ 'is-active': $route.path === item.path }"
        :aria-current="$route.path === item.path ? 'page' : undefined"
        @click="handleNavClick(item)"
      >
        <span class="nav-title">{{ item.title }}</span>
        <span
          v-if="item.badge"
          class="nav-badge"
          :class="`nav-badge--${String(item.badge).toLowerCase()}`"
        >
          {{ item.badge }}
        </span>
      </router-link>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { trackRumClick } from '@/utils/rum'

interface NavItem {
  name: string
  path: string
  title: string
  icon?: string
  badge?: string | number
}

interface Props {
  pageList: NavItem[]
  isMobile?: boolean
}

defineProps<Props>()

const handleNavClick = (item: NavItem) => {
  trackRumClick('top_nav', 'navigate', {
    target: item.name,
    target_title: item.title,
    target_path: item.path,
    badge: item.badge ? String(item.badge) : undefined
  })
}
</script>

<style scoped>
.ah-nav {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  min-width: 0;
  margin-left: 32px;
  flex-shrink: 0;
}

.ah-nav-menu {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 6px;
  border: 1px solid rgba(72, 134, 255, 0.14);
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(239, 247, 255, 0.72)),
    rgba(255, 255, 255, 0.78);
  box-shadow: 0 10px 30px rgba(72, 134, 255, 0.08);
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ah-nav-menu::-webkit-scrollbar {
  display: none;
}

.ah-nav-item {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 18px;
  border: 1px solid transparent;
  border-radius: 14px;
  color: #46627f;
  text-decoration: none;
  font-size: 14px;
  font-weight: 650;
  line-height: 20px;
  letter-spacing: 0;
  white-space: nowrap;
  flex-shrink: 0;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.nav-title {
  position: relative;
  z-index: 1;
  display: inline-block;
}

.nav-badge {
  position: absolute;
  top: -8px;
  right: -6px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 17px;
  padding: 0 6px;
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 8px 8px 8px 3px;
  color: #ffffff;
  background: linear-gradient(135deg, #ff5b6d 0%, #ff3159 52%, #ff7a45 100%);
  box-shadow: 0 6px 14px rgba(255, 49, 89, 0.24);
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0;
  text-transform: uppercase;
  transform: rotate(5deg);
  transform-origin: center;
}

.nav-badge--new {
  color: #0b75d1;
  background: linear-gradient(135deg, #e6f6ff 0%, #aee5ff 48%, #6fc7ff 100%);
  box-shadow: 0 6px 14px rgba(50, 168, 255, 0.2);
}

.nav-badge--hot::after {
  content: '';
  position: absolute;
  right: 4px;
  bottom: -3px;
  z-index: -1;
  width: 7px;
  height: 7px;
  border-right: 1px solid rgba(255, 255, 255, 0.72);
  border-bottom: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 0 0 2px 0;
  background: #ff4e62;
  transform: rotate(45deg);
}

.ah-nav-item:hover {
  color: #147de8;
  border-color: rgba(109, 190, 255, 0.28);
  background: rgba(232, 246, 255, 0.9);
  box-shadow: 0 8px 20px rgba(72, 134, 255, 0.11);
  transform: translateY(-1px);
}

.ah-nav-item:focus-visible {
  outline: 2px solid rgba(64, 169, 255, 0.76);
  outline-offset: 2px;
}

.ah-nav-item.is-active {
  color: #0f72d9;
  border-color: rgba(74, 169, 255, 0.34);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(226, 246, 255, 0.96)),
    #e8f6ff;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 10px 24px rgba(72, 134, 255, 0.16);
}

.ah-nav-item.is-active::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 50%;
  width: 22px;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, #7bd7ff, #2f91ff);
  box-shadow: 0 2px 8px rgba(47, 145, 255, 0.32);
  transform: translateX(-50%);
}

.ah-nav-item.is-active .nav-title {
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.9);
}

@media (max-width: 480px) {
  .ah-nav {
    margin-left: 16px;
  }

  .ah-nav-menu {
    gap: 6px;
    padding: 9px 8px 5px;
    border-radius: 16px;
  }

  .ah-nav-item {
    min-height: 34px;
    padding: 7px 13px;
    font-size: 13px;
  }

  .nav-badge {
    top: -7px;
    right: -5px;
    min-width: 28px;
    height: 16px;
    font-size: 9px;
  }
}
</style>
